import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserVerifications, unverifyUserIdentity, fetchUserDetail } from '../../../../../redux/slice/allUsersSlice';
import CustomTable from '../../../../../components/table/CustomTable';
import ConfirmModal from '../../../../../components/withModal/ConfirmModal';
import { formatMomentDate } from '../../../../../utils/dateHelper';
import { useToast } from '../../../../../context/TostContext';
import StatusChip from '../../../../../components/statusChip/StatusChip';

const getStatusBg = (status) => {
    const lowerStatus = status?.toLowerCase();
    switch (lowerStatus) {
        case "approved":
        case "verified":
        case "paid":
            return "success";
        case "pending":
        case "in_progress":
            return "blue";
        case "rejected":
        case "failed":
        case "expired":
        case "cancelled":
            return "warning";
        case "revoked":
            return "danger";
        default:
            return "";
    }
};

const parsePaymentDetails = (details, group) => {
    if (!details) return "-";

    const method = details.payment_method || details;

    if (group === "upi" && method.upi) {
        return `UPI ID: ${method.upi.upi_id || "N/A"}`;
    }
    if (group === "card" && method.card) {
        const { card_bank_name, card_network, card_type, card_number } = method.card;
        const typeFormatted = card_type ? card_type.replace('_', ' ') : '';
        return [card_bank_name, card_network, typeFormatted, `ending in ${card_number}`].filter(Boolean).join(" ").toUpperCase();
    }
    return group ? group.toUpperCase() : "See details";
};

const Verifications = ({ userDetail }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [confirmModalConfig, setConfirmModalConfig] = useState({
        isOpen: false,
        title: "",
        message: "",
        onConfirm: () => { },
        confirmText: "Yes",
        cancelText: "No",
    });

    const handleRevokeVerification = (row) => {
        setConfirmModalConfig({
            isOpen: true,
            title: "Revoke Verification",
            message: "Are you sure you want to revoke this user's manual verification? The user will no longer be marked as identity verified.",
            confirmText: "Revoke",
            cancelText: "Cancel",
            onConfirm: () => {
                dispatch(unverifyUserIdentity(userDetail.id))
                    .unwrap()
                    .then(() => {
                        toast.success("User identity unverified successfully");
                        setConfirmModalConfig((prev) => ({ ...prev, isOpen: false }));
                        dispatch(fetchUserDetail(userDetail.id));
                        dispatch(fetchUserVerifications({ id: userDetail.id, page, limit }));
                    })
                    .catch((error) => {
                        toast.error(error?.message || "Failed to unverify identity");
                        setConfirmModalConfig((prev) => ({ ...prev, isOpen: false }));
                    });
            }
        });
    };

    const {
        userVerifications,
        userVerificationsLoading,
        userVerificationsTotal
    } = useSelector(state => state.allUsers);

    useEffect(() => {
        if (userDetail?.id) {
            dispatch(fetchUserVerifications({ id: userDetail.id, page, limit }));
        }
    }, [dispatch, userDetail?.id, page, limit]);

    // const handleVerifyIdentity = () => {
    //     if (!userDetail) return;
    //     dispatch(manualVerifyUserIdentity(userDetail.id))
    //         .unwrap()
    //         .then(() => {
    //             toast.success("User identity verified successfully");
    //             dispatch(fetchUserVerifications({ id: userDetail.id, page, limit }));
    //         })
    //         .catch((error) => {
    //             toast.error(error?.message || "Failed to verify identity");
    //         });
    // };

    const getLatestIdentity = (item) => {
        if (!item.identities || item.identities.length === 0) return null;
        return [...item.identities].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    };

    const getLatestPayment = (item) => {
        if (!item.payments || item.payments.length === 0) return null;
        return [...item.payments].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    };
    const actionList = [
        {
            title: "View",
            handler: (row) => { }
        },
        {
            title: "Revoke",
            isVisible: (row) => row.overall_status === 'verified',
            handler: (row) => {
                handleRevokeVerification(row);
            }
        }
    ];
    const columns = [

        {
            header: 'Created By',
            cell: (_, item) => {
                if (item.verified_by_type === 'admin') {
                    const adminName = item.verifiedByAdmin?.name || item.verifiedByAdmin?.email || 'Admin';
                    return <span className="text-success fw-medium">Admin ({adminName})</span>;
                }
                if (item.verified_by_type === 'user') {
                    return <span className="text-muted">User (Self)</span>;
                }
                return <span>-</span>;
            }
        },
        {
            header: 'Overall Status',
            accessor: 'overall_status',
            cell: (v) => (
                v ? (
                    <StatusChip
                        bg={getStatusBg(v)}
                        pill
                        title={v}
                        className="text-capitalize"
                    />
                ) : "-"
            )
        },
        {
            header: 'Verified At',
            cell: (_, item) => item.verified_at ? formatMomentDate(item.verified_at, "DD MMM YYYY, hh:mm A") : "-"
        },
        {
            header: 'Expires At',
            cell: (_, item) => item.expires_at ? formatMomentDate(item.expires_at, "DD MMM YYYY, hh:mm A") : "-"
        },
        {
            header: 'Revoked By',
            cell: (_, item) => {
                if (item.overall_status === 'revoked' && item.revoked_by) {
                    const adminName = item.revoked_by.name || item.revoked_by.email || 'Admin';
                    return <span className="text-danger fw-medium">Admin ({adminName})</span>;
                }
                return "-";
            }
        },
        {
            header: 'Revoked At',
            cell: (_, item) => (
                item.overall_status === 'revoked' && item.revoked_at ? (
                    formatMomentDate(item.revoked_at, "DD MMM YYYY, hh:mm A")
                ) : "-"
            )
        },
        {
            header: 'Identity Source',
            cell: (_, item) => {
                const identity = getLatestIdentity(item);
                return identity?.verification_source ? (
                    <span className="text-capitalize text-secondary">
                        {identity.verification_source.replace('_', ' ')}
                    </span>
                ) : "-";
            }
        },
        {
            header: 'Doc Type',
            cell: (_, item) => {
                const identity = getLatestIdentity(item);
                return identity ? <div className="fw-medium">{identity.document_type || "-"}</div> : "-";
            }
        },
        {
            header: 'Doc No',
            cell: (_, item) => {
                const identity = getLatestIdentity(item);
                return identity ? <div>{identity.masked_aadhaar || identity.document_no || "-"}</div> : "-";
            }
        },
        {
            header: 'Identity Status',
            cell: (_, item) => {
                const identity = getLatestIdentity(item);
                return identity?.status ? (
                    <StatusChip
                        bg={getStatusBg(identity.status)}
                        pill
                        title={identity.status}
                        className="text-capitalize"
                    />
                ) : "-";
            }
        },
        {
            header: 'Identity Verified At',
            cell: (_, item) => {
                const identity = getLatestIdentity(item);
                return identity?.verified_at ? formatMomentDate(identity.verified_at, "DD MMM YYYY, hh:mm A") : "-";
            }
        },
        {
            header: 'Identity Verified By',
            cell: (_, item) => {
                const identity = getLatestIdentity(item);
                if (!identity) return "-";
                if (identity.verified_by_type === 'admin') {
                    const adminName = identity.verifiedByAdmin?.name || identity.verifiedByAdmin?.email || 'Admin';
                    return <span className="text-success fw-medium">Admin ({adminName})</span>;
                }
                if (identity.verified_by_type === 'user') {
                    return <span className="text-muted">User (Self)</span>;
                }
                return "-";
            }
        },
        {
            header: 'Payment Source',
            cell: (_, item) => {
                const payment = getLatestPayment(item);
                return payment?.payment_source ? (
                    <span className="text-capitalize text-secondary">
                        {payment.payment_source.replace('_', ' ')}
                    </span>
                ) : "-";
            }
        },
        {
            header: 'Payment Amount',
            cell: (_, item) => {
                const payment = getLatestPayment(item);
                return payment ? <div>{`${payment.amount || 0} ${payment.currency || 'INR'}`}</div> : "-";
            }
        },
        {
            header: 'Payment Status',
            cell: (_, item) => {
                const payment = getLatestPayment(item);
                return payment?.status ? (
                    <StatusChip
                        bg={getStatusBg(payment.status)}
                        pill
                        title={payment.status}
                        className="text-capitalize"
                    />
                ) : "-";
            }
        },
        {
            header: 'Payment Method',
            cell: (_, item) => {
                const payment = getLatestPayment(item);
                if (!payment) return "-";

                const method = payment.payment_method;
                if (method && typeof method === 'object') {
                    const keys = Object.keys(method);
                    if (keys.length > 0) {
                        return <span className="text-capitalize">{keys[0]}</span>;
                    }
                }
                return typeof method === 'string' ? method : (payment.payment_group || "-");
            }
        },
        {
            header: 'Payment Method Details',
            cell: (_, item) => {
                const payment = getLatestPayment(item);
                if (!payment) return "-";

                // if (payment.payment_message) {
                //     return <div className="text-nowrap small">{payment.payment_message}</div>;
                // }

                const details = payment.payment_details || payment.payment_method;
                if (!details) return "-";

                const group = payment.payment_group ||
                    payment.payment_method?.type ||
                    (payment.payment_method ? Object.keys(payment.payment_method)[0] : undefined);

                return <div className="text-nowrap small">{parsePaymentDetails(details, group)}</div>;
            }
        },
        {
            header: 'Payment ID',
            cell: (_, item) => {
                const payment = getLatestPayment(item);
                return payment ? <div>{payment.cf_payment_id || payment.cashfree_order_id || "-"}</div> : "-";
            }
        },
        {
            header: 'Paid At',
            cell: (_, item) => {
                const payment = getLatestPayment(item);
                return payment?.paid_at ? formatMomentDate(payment.paid_at, "DD MMM YYYY, hh:mm A") : "-";
            }
        },
        {
            header: 'Payment Verified By',
            cell: (_, item) => {
                const payment = getLatestPayment(item);
                if (!payment) return "-";
                if (payment.verified_by_type === 'admin') {
                    const adminName = payment.verifiedByAdmin?.name || payment.verifiedByAdmin?.email || 'Admin';
                    return <span className="text-success fw-medium">Admin ({adminName})</span>;
                }
                if (payment.verified_by_type === 'user') {
                    return <span className="text-muted">User (Self)</span>;
                }
                return "-";
            }
        }
    ];

    return (
        <div className="bg-white rounded-3 p-4 shadow-sm">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h6 className="mb-0">User Verifications</h6>
            </div>
            <CustomTable
                columns={columns}
                data={userVerifications}
                loading={userVerificationsLoading}
                actionList={actionList}
                pagination={{
                    page,
                    limit,
                    total: userVerificationsTotal || 0,
                    totalPages: Math.ceil((userVerificationsTotal || 0) / limit),
                }}
                onPageChange={(newPage) => setPage(newPage)}
                onLimitChange={(newLimit) => {
                    setLimit(newLimit);
                    setPage(1); // Reset to page 1 on limit change
                }}
                noDataMessage="No verification records found."
            />
            <ConfirmModal
                isOpen={confirmModalConfig.isOpen}
                onClose={() => setConfirmModalConfig((prev) => ({ ...prev, isOpen: false }))}
                title={confirmModalConfig.title}
                message={confirmModalConfig.message}
                onConfirm={confirmModalConfig.onConfirm}
                confirmText={confirmModalConfig.confirmText}
                cancelText={confirmModalConfig.cancelText}
                className='confirm-modal'
                size='sm'
            />
        </div>
    );
};

export default Verifications;
